import React from 'react';

const routes = {
  Root: {
    path: '/users',
    component: React.lazy(() =>
      import('../components/Pages/Users' /* webpackChunkName: "Root" */),
    ),
  },

  Auth: {
    Login: {
      path: '/login',
      component: React.lazy(() =>
        import('../components/Pages/Auth/Login' /* webpackChunkName: "AuthLogin" */),
      ),
    },
  },

  Administration: {
    path: '/administration',
    component: React.lazy(() =>
      import(
        '../components/Pages/Administration' /* webpackChunkName: "Administration" */
      ),
    ),
  },

  FiatTransfer: {
    Pending: {
      path: '/fiat_transfer/pending',
      component: React.lazy(() =>
        import(
          '../components/Pages/FiatTransfer/Pending' /* webpackChunkName: "Pending_Fiat" */
        ),
      ),
    },
    Deposit: {
      path: '/fiat_transfer/deposit',
      component: React.lazy(() =>
        import(
          '../components/Pages/FiatTransfer/Deposit' /* webpackChunkName: "Deposit_Fiat" */
        ),
      ),
    },
    Withdrawals: {
      path: '/fiat_transfer/withdrawals',
      component: React.lazy(() =>
        import(
          '../components/Pages/FiatTransfer/Withdrawals' /* webpackChunkName: "Withdrawals_Fiat" */
        ),
      ),
    },
  },

  CryptoTransfer: {
    Pending: {
      path: '/crypto_transfer/pending',
      component: React.lazy(() =>
        import(
          '../components/Pages/CryptoTransfer/Pending' /* webpackChunkName: "Pending" */
        ),
      ),
    },
    Deposit: {
      path: '/crypto_transfer/deposit',
      component: React.lazy(() =>
        import(
          '../components/Pages/CryptoTransfer/Deposit' /* webpackChunkName: "Deposit" */
        ),
      ),
    },
    Withdrawals: {
      path: '/crypto_transfer/withdrawals',
      component: React.lazy(() =>
        import(
          '../components/Pages/CryptoTransfer/Withdrawals' /* webpackChunkName: "Withdrawals" */
        ),
      ),
    },
  },

  Market: {
    FeesAndLimits: {
      path: '/market/fees_and_limits',
      component: React.lazy(() =>
        import(
          '../components/Pages/Market/FeesAndLimits' /* webpackChunkName: "FeesAndLimits" */
        ),
      ),
    },
    TradeVolumeSettings: {
      path: '/market/trade_volume_settings',
      component: React.lazy(() =>
        import(
          '../components/Pages/Market/TradeVolumeSettings' /* webpackChunkName: "TradeVolumeSettings" */
        ),
      ),
    },
    Currencies: {
      path: '/market/currencies',
      component: React.lazy(() =>
        import(
          '../components/Pages/Market/Currencies' /* webpackChunkName: "Currencies" */
        ),
      ),
    },
    Fees: {
      path: '/market/fees',
      component: React.lazy(() =>
        import('../components/Pages/Market/Fees' /* webpackChunkName: "Fees" */),
      ),
    },
    Limitations: {
      path: '/market/limitations',
      component: React.lazy(() =>
        import(
          '../components/Pages/Market/Limitations' /* webpackChunkName: "Limitations" */
        ),
      ),
    },
    Robot: {
      path: '/market/market_maker_robot',
      component: React.lazy(() =>
        import('../components/Pages/Market/Robot' /* webpackChunkName: "Robot" */),
      ),
    },
    Tokens: {
      path: '/market/tokens',
      component: React.lazy(() =>
        import('../components/Pages/Market/Tokens' /* webpackChunkName: "Tokens" */),
      ),
    },
    TradingPairs: {
      path: '/market/trading_pairs',
      component: React.lazy(() =>
        import(
          '../components/Pages/Market/TradingPairs' /* webpackChunkName: "TradingPairs" */
        ),
      ),
    },
  },

  Orders: {
    path: '/orders',
    component: React.lazy(() =>
      import('../components/Pages/Orders' /* webpackChunkName: "Orders" */),
    ),
  },

  Videos: {
    path: '/videos',
    component: React.lazy(() =>
      import('../components/Pages/General/Videos' /* webpackChunkName: "Videos" */),
    ),
  },

  Vacancies: {
    path: '/vacancies',
    component: React.lazy(() =>
      import(
        '../components/Pages/General/Vacancies' /* webpackChunkName: "Vacancies" */
      ),
    ),
    NewVacancies: {
      path: '/vacancies/new',
      component: React.lazy(() =>
        import(
          '../components/Pages/General/Vacancies/NewVacancies' /* webpackChunkName: "NewVacancies" */
        ),
      ),
    },
    EditVacancies: {
      path: '/vacancies/edit/', // :id
      component: React.lazy(() =>
        import(
          '../components/Pages/General/Vacancies/EditVacancies' /* webpackChunkName: "EditVacancies" */
        ),
      ),
    },
  },

  Posts: {
    path: '/posts',
    component: React.lazy(() =>
      import('../components/Pages/General/Posts' /* webpackChunkName: "Posts" */),
    ),
    NewPost: {
      path: '/posts/new',
      component: React.lazy(() =>
        import(
          '../components/Pages/General/Posts/NewPost' /* webpackChunkName: "NewPost" */
        ),
      ),
    },
    NewPostContent: {
      path: '/posts/new_content/', // :id
      component: React.lazy(() =>
        import(
          '../components/Pages/General/Posts/NewPostContent' /* webpackChunkName: "NewPostContent" */
        ),
      ),
    },
    EditPost: {
      path: '/posts/edit/', // :id and :lang
      component: React.lazy(() =>
        import(
          '../components/Pages/General/Posts/EditPost' /* webpackChunkName: "EditPost" */
        ),
      ),
    },
    Categories: {
      path: '/posts/categories',
      component: React.lazy(() =>
        import(
          '../components/Pages/General/Posts/Categories' /* webpackChunkName: "PostCategories" */
        ),
      ),
      NewCategory: {
        path: '/posts/categories/new',
        component: React.lazy(() =>
          import(
            '../components/Pages/General/Posts/Categories/NewCategory' /* webpackChunkName: "NewPostCategory" */
          ),
        ),
      },
      EditCategory: {
        path: '/posts/categories/edit/', // :id
        component: React.lazy(() =>
          import(
            '../components/Pages/General/Posts/Categories/EditCategory' /* webpackChunkName: "EditPostCategory" */
          ),
        ),
      },
      NewCategoryContent: {
        path: '/posts/categories/new_content/', // :id
        component: React.lazy(() =>
          import(
            '../components/Pages/General/Posts/Categories/NewCategoryContent' /* webpackChunkName: "NewPostCategoryContent" */
          ),
        ),
      },
      EditCategoryContent: {
        path: '/posts/categories/edit/', // :id and :lang
        component: React.lazy(() =>
          import(
            '../components/Pages/General/Posts/Categories/EditCategoryContent' /* webpackChunkName: "EditPostCategoryContent" */
          ),
        ),
      },
    },
  },

  Vote: {
    path: '/vote_projects',
    component: React.lazy(() =>
      import('../components/Pages/Vote' /* webpackChunkName: "VoteProjects" */),
    ),
    NewVoteProject: {
      path: '/vote_projects/new',
      component: React.lazy(() =>
        import(
          '../components/Pages/Vote/NewVoteProject' /* webpackChunkName: "NewVoteProject" */
        ),
      ),
    },
    NewVoteProjectContent: {
      path: '/vote_projects/new_content/', // :id
      component: React.lazy(() =>
        import(
          '../components/Pages/Vote/NewVoteProjectContent' /* webpackChunkName: "NewVoteProjectContent" */
        ),
      ),
    },
    EditVoteProjectContent: {
      path: '/vote_projects/edit_content/', // :id and :lang
      component: React.lazy(() =>
        import(
          '../components/Pages/Vote/EditVoteProjectContent' /* webpackChunkName: "EditVoteProjectContent" */
        ),
      ),
    },
    EditVoteProject: {
      path: '/vote_projects/edit/', // :id
      component: React.lazy(() =>
        import(
          '../components/Pages/Vote/EditVoteProject' /* webpackChunkName: "EditVoteProject" */
        ),
      ),
    },
    VoteProjectBalance: {
      path: '/vote_project_balance',
      component: React.lazy(() =>
        import(
          '../components/Pages/Vote/ProjectBalance' /* webpackChunkName: "VoteProjectBalance" */
        ),
      ),
    },
  },

  FutureVote: {
    path: '/vote_future_projects',
    component: React.lazy(() =>
      import(
        '../components/Pages/FutureVote' /* webpackChunkName: "FutureVoteProjects" */
      ),
    ),
    NewFutureVoteProject: {
      path: '/vote_future_projects/new',
      component: React.lazy(() =>
        import(
          '../components/Pages/FutureVote/NewFutureVoteProject' /* webpackChunkName: "NewFutureVoteProject" */
        ),
      ),
    },
    NewFutureVoteProjectContent: {
      path: '/vote_future_projects/new_content/', // :id
      component: React.lazy(() =>
        import(
          '../components/Pages/FutureVote/NewFutureVoteProjectContent' /* webpackChunkName: "NewFutureVoteProjectContent" */
        ),
      ),
    },
    EditFutureVoteProjectContent: {
      path: '/vote_future_projects/edit_content/', // :id and :lang
      component: React.lazy(() =>
        import(
          '../components/Pages/FutureVote/EditFutureVoteProjectContent' /* webpackChunkName: "EditFutureVoteProjectContent" */
        ),
      ),
    },
    EditFutureVoteProject: {
      path: '/vote_future_projects/edit/', // :id
      component: React.lazy(() =>
        import(
          '../components/Pages/FutureVote/EditFutureVoteProject' /* webpackChunkName: "EditFutureVoteProject" */
        ),
      ),
    },
  },

  IEOListing: {
    path: '/vote_ieo_projects',
    component: React.lazy(() =>
      import(
        '../components/Pages/IEOListing' /* webpackChunkName: "VoteIEOListingProjects" */
      ),
    ),
    NewProject: {
      path: '/vote_ieo_projects/new_project',
      component: React.lazy(() =>
        import(
          '../components/Pages/Projects' /* webpackChunkName: "Administration" */
        ),
      ),
    },
    Project: {
      path: '/projects/', // :id
      component: React.lazy(() =>
        import(
          '../components/Pages/ShowProjects' /* webpackChunkName: "Administration" */
        ),
      ),
    },
    NewVoteIEOProject: {
      path: '/vote_ieo_projects/new',
      component: React.lazy(() =>
        import(
          '../components/Pages/IEOListing/NewVoteIEOProject' /* webpackChunkName: "NewVoteIEOProject" */
        ),
      ),
    },
    NewVoteIEOProjectContent: {
      path: '/vote_ieo_projects/new_content/', // :id
      component: React.lazy(() =>
        import(
          '../components/Pages/IEOListing/NewVoteIEOProjectContent' /* webpackChunkName: "NewVoteIEOProjectContent" */
        ),
      ),
    },
    EditVoteIEOProjectContent: {
      path: '/vote_ieo_projects/edit_content/', // :id and :lang
      component: React.lazy(() =>
        import(
          '../components/Pages/IEOListing/EditVoteIEOProjectContent' /* webpackChunkName: "EditVoteIEOProjectContent" */
        ),
      ),
    },
    EditVoteIEOProject: {
      path: '/vote_ieo_projects/edit/', // :id
      component: React.lazy(() =>
        import(
          '../components/Pages/IEOListing/EditVoteIEOProject' /* webpackChunkName: "EditVoteIEOProject" */
        ),
      ),
    },
  },

  Cashbacks: {
    CashbackList: {
      path: '/cashbacks_list',
      component: React.lazy(() =>
        import(
          '../components/Pages/Users/Cashbacks/CashbackList' /* webpackChunkName: "CashbackList" */
        ),
      ),
    },
    Statistics: {
      path: '/cashbacks_statistics',
      component: React.lazy(() =>
        import(
          '../components/Pages/Users/Cashbacks/Statistics' /* webpackChunkName: "CashbackStatistics" */
        ),
      ),
    },
  },

  Trades: {
    path: '/trades',
    component: React.lazy(() =>
      import('../components/Pages/Trades' /* webpackChunkName: "Trades" */),
    ),
  },

  TraderInfo: {
    path: '/traderinfo',
    component: React.lazy(() =>
      import(
        '../components/Pages/General/TraderInfo' /* webpackChunkName: "TraderInfo" */
      ),
    ),
    EditTraderInfo: {
      path: '/traderinfo/edit/', // :id
      component: React.lazy(() =>
        import(
          '../components/Pages/General/TraderInfo/EditTraderInfo' /* webpackChunkName: "EditTraderInfo" */
        ),
      ),
    },
  },

  ReferralSystem: {
    path: '/referral_system',
    component: React.lazy(() =>
      import(
        '../components/Pages/Users/ReferralSystem' /* webpackChunkName: "ReferralSystem" */
      ),
    ),
  },

  Users: {
    path: '/users',
    component: React.lazy(() =>
      import('../components/Pages/Users' /* webpackChunkName: "Users" */),
    ),
    Profile: {
      path: '/users/', // :id
      component: React.lazy(() =>
        import(
          '../components/Pages/Users/Profile' /* webpackChunkName: "Profile" */
        ),
      ),
    },
  },

  ChatAdminUsers: {
    path: '/chat_admin_users',
    component: React.lazy(() =>
      import(
        '../components/Pages/Users/ChatAdminUsers' /* webpackChunkName: "ChatAdminUsers" */
      ),
    ),
  },

  Wallets: {
    Wallets: {
      path: '/wallets/wallets',
      component: React.lazy(() =>
        import(
          '../components/Pages/Wallets/Wallets' /* webpackChunkName: "Wallets" */
        ),
      ),
    },
    Transactions: {
      path: '/wallets/transactions',
      component: React.lazy(() =>
        import(
          '../components/Pages/Wallets/Transactions' /* webpackChunkName: "Transactions" */
        ),
      ),
    },
    Profits: {
      path: '/wallets/profits',
      component: React.lazy(() =>
        import(
          '../components/Pages/Wallets/Profits' /* webpackChunkName: "Profits" */
        ),
      ),
    },
    Bnixfee: {
      path: '/wallets/bnix_fee',
      component: React.lazy(() =>
        import(
          '../components/Pages/Wallets/BnixFee' /* webpackChunkName: "BnixFee" */
        ),
      ),
    },
    GasFeeSettings: {
      path: '/wallets/gas_fee_settings',
      component: React.lazy(() =>
        import(
          '../components/Pages/Wallets/GasFeeSettings' /* webpackChunkName: "GasFeeSettings" */
        ),
      ),
    },
  },

  HistoryReplenishment: {
    path: '/history_replenishment',
    component: React.lazy(() =>
      import(
        '../components/Pages/Users/HistoryReplenishment' /* webpackChunkName: "HistoryReplenishment" */
      ),
    ),
  },

  Settings: {
    // BankAccounts: {
    //   path: '/bank_accounts',
    //   component: React.lazy(() =>
    //     import(
    //       '../components/Pages/Settings/BankAccounts' /* webpackChunkName: "BankAccounts" */
    //     ),
    //   ),
    // },
    Security: {
      path: '/security',
      component: React.lazy(() =>
        import(
          '../components/Pages/Settings/Security' /* webpackChunkName: "Security" */
        ),
      ),
    },
  },
  Reset2FA: {
    path: '/reset_2FA',
    component: React.lazy(() =>
      import('../components/Pages/Reset2FA' /* webpackChunkName: "Reset2FA" */),
    ),
  },
};

export default routes;
